import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePanelToast } from '../../context/PanelToastContext';
import PageHeader from '../../components/owner/PageHeader';
import PasswordField from '../auth/PasswordField';
import { fetchOwnerProfile, saveOwnerProfile } from '../../services/owner/profileApi';
import './ownerPages.css';

function Profile() {
  const { user, account, updateAccount } = useAuth();
  const toast = usePanelToast();
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', role: 'Owner' });
  const [currentPassword, setCurrentPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchOwnerProfile(user?.id);
        if (!cancelled) {
          setProfile({
            name: data.name || account?.full_name || '',
            email: data.email || account?.email || user?.email || '',
            phone: data.phone || '',
            role: 'Owner',
          });
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.email, account?.full_name, account?.email]);

  const updateProfile = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const updated = await saveOwnerProfile(user?.id, profile, currentPassword);
      setProfile(updated);
      setCurrentPassword('');
      updateAccount({
        full_name: updated.name,
        email: updated.email,
      });
      toast.showSaved('Profile');
    } catch (err) {
      setError(err.message || 'Failed to save profile');
      toast.showError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Profile" subtitle="Manage your owner account details" variant="banner" />

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading profile...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="owner-card owner-mb-28 rec-panel-card">
        <h2 className="owner-card-title">Update Profile</h2>
        <p className="owner-placeholder-text" style={{ marginTop: 0, marginBottom: 16 }}>
          Changes require your current password. Only your signed-in owner account can be updated.
        </p>

        <div className="owner-grid-2">
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="full-name">Full Name</label>
            <input
              id="full-name"
              className="owner-form-input"
              value={profile.name}
              onChange={(e) => updateProfile('name', e.target.value)}
              autoComplete="name"
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className="owner-form-input"
              value={profile.email}
              onChange={(e) => updateProfile('email', e.target.value)}
              autoComplete="email"
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="phone">Phone</label>
            <input
              id="phone"
              type="tel"
              className="owner-form-input"
              value={profile.phone}
              onChange={(e) => updateProfile('phone', e.target.value)}
              autoComplete="tel"
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="role">Role</label>
            <input id="role" className="owner-form-input" value={profile.role} readOnly />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <PasswordField
              id="current-password"
              name="currentPassword"
              label="Current Password (required to authorize)"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
        </div>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={handleSave}
          disabled={saving || loading || !currentPassword}
        >
          {saving ? 'Saving…' : 'Update Profile'}
        </button>
      </div>
    </div>
  );
}

export default Profile;
